import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Form, Modal, Select, Spin, Table } from 'antd';
import { useTranslate } from '@/translations';
import { DragUpload } from '@/ui';
import { inferEndVin, REFERENCE_TYPES } from './manifestEditor';
import {
  ImportedReference,
  ParsedReferenceSheet,
  ReferenceColumnMap,
  parseReferenceWorkbook,
  resolveReferenceColumnMap,
  rowsFromColumnMap,
  summarizeReferenceRows,
} from './referenceImport';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

type ReferenceImportModalProps = {
  open: boolean;
  multiple: boolean;
  file?: File;
  onCancel: () => void;
  onImport: (rows: ImportedReference[]) => void;
};

export const ReferenceImportModal = ({
  open,
  multiple,
  file,
  onCancel,
  onImport,
}: ReferenceImportModalProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [form] = Form.useForm<{
    maps?: Record<string, ReferenceColumnMap>;
    types?: Record<string, string | undefined>;
  }>();
  const watchedMaps = Form.useWatch('maps', form);
  const watchedTypes = Form.useWatch('types', form);
  const [sheets, setSheets] = useState<ParsedReferenceSheet[]>([]);
  const [parsing, setParsing] = useState(false);
  const requestId = useRef(0);

  const reset = () => {
    setSheets([]);
    form.resetFields();
    setParsing(false);
  };

  const loadFiles = async (next: File[]) => {
    const id = requestId.current + 1;
    requestId.current = id;
    const accepted = next.filter((item) => {
      if (item.size <= MAX_FILE_BYTES) return true;
      message.error(t('fileIsTooLarge'));
      return false;
    });
    const chosen = multiple ? accepted : accepted.slice(0, 1);
    if (!chosen.length) {
      if (requestId.current === id) reset();
      return;
    }
    setParsing(true);
    try {
      const parsed = (
        await Promise.all(chosen.map((item) => parseReferenceWorkbook(item)))
      ).flat();
      if (requestId.current !== id) return;
      setSheets(parsed);
      form.setFieldsValue({
        maps: Object.fromEntries(
          parsed.map((sheet) => [sheet.fileName, { ...sheet.suggested }]),
        ),
        types: {},
      });
    } catch {
      if (requestId.current !== id) return;
      message.error(t('generalError'));
      reset();
    } finally {
      if (requestId.current === id) setParsing(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (file) void loadFiles([file]);
    return () => {
      requestId.current += 1;
      setSheets([]);
      form.resetFields();
    };
    // Reload only when the dialog opens or the seeded file changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  const columnMap = (sheet: ParsedReferenceSheet) =>
    resolveReferenceColumnMap(sheet, watchedMaps?.[sheet.fileName]);

  const preview = useMemo(() => {
    const types = watchedTypes ?? {};
    const extracted = sheets.flatMap((sheet) =>
      rowsFromColumnMap(
        sheet.rows,
        resolveReferenceColumnMap(sheet, watchedMaps?.[sheet.fileName]),
        sheet.referenceFromSheetName ? sheet.sheetName : undefined,
      ),
    );
    return summarizeReferenceRows(extracted).map((row) => {
      const key = row.reference.toUpperCase();
      if (!Object.prototype.hasOwnProperty.call(types, key)) return row;
      return { ...row, referenceType: types[key] || undefined };
    });
  }, [sheets, watchedMaps, watchedTypes]);

  const needsColumnMatch = sheets.some((sheet) => {
    const map = columnMap(sheet);
    if (!map.vin) return true;
    return !sheet.referenceFromSheetName && !map.shippingReference;
  });
  const columnOptions = (headers: string[]) =>
    headers.map((header) => ({ value: header, label: header }));

  return (
    <Modal
      title={multiple ? t('importReferences') : t('importReferenceFile')}
      open={open}
      width={multiple ? 960 : undefined}
      onCancel={onCancel}
      okText={t('importReferences')}
      okButtonProps={{
        disabled: parsing || needsColumnMatch || preview.length === 0,
        loading: parsing,
      }}
      onOk={() => onImport(preview)}
      destroyOnHidden
    >
      <Form form={form} component="div">
        {open ? (
          <DragUpload
            multiple={multiple}
            accept=".csv,.xlsx,.xls"
            maxFileSize={MAX_FILE_BYTES}
            invalidMaxFileSizeMessage={t('fileIsTooLarge')}
            showUploadBtn={false}
            uploadText={
              multiple ? t('importReferences') : t('importReferenceFile')
            }
            onFileChanged={(list) => {
              const next = list.flatMap((item) =>
                item.originFileObj ? [item.originFileObj] : [],
              );
              void loadFiles(next);
            }}
          />
        ) : null}
        {parsing ? <Spin style={{ display: 'block', marginTop: 12 }} /> : null}
        {sheets.map((sheet) => {
          const options = columnOptions(sheet.headers);
          return (
            <div key={sheet.fileName} style={{ marginTop: 12 }}>
              {sheets.length > 1 ? <strong>{sheet.fileName}</strong> : null}
              {sheet.referenceFromSheetName ? (
                <p style={{ marginTop: 8 }}>
                  {t('sheetReferenceName', { name: sheet.sheetName })}
                </p>
              ) : null}
              {!sheet.referenceFromSheetName &&
              (!sheet.suggested.shippingReference || !sheet.suggested.vin) ? (
                <>
                  <Form.Item
                    name={['maps', sheet.fileName, 'shippingReference']}
                    label={t('chooseShippingReferenceColumn')}
                  >
                    <Select options={options} />
                  </Form.Item>
                  <Form.Item
                    name={['maps', sheet.fileName, 'vin']}
                    label={t('chooseVinColumn')}
                  >
                    <Select options={options} />
                  </Form.Item>
                </>
              ) : null}
              {!sheet.suggested.referenceType ? (
                <Form.Item
                  name={['maps', sheet.fileName, 'referenceType']}
                  label={t('chooseReferenceTypeColumn')}
                >
                  <Select allowClear options={options} />
                </Form.Item>
              ) : null}
            </div>
          );
        })}
        {needsColumnMatch && sheets.length ? (
          <p style={{ marginTop: 12 }}>{t('columnMatchRequired')}</p>
        ) : null}
        {sheets.length &&
        !parsing &&
        !needsColumnMatch &&
        preview.length === 0 ? (
          <p style={{ marginTop: 12 }}>{t('noReferencesInFile')}</p>
        ) : null}
        {preview.length ? (
          <Table
            style={{ marginTop: 12 }}
            size="small"
            pagination={false}
            rowKey="reference"
            dataSource={preview}
            columns={[
              { title: t('shippingReference'), dataIndex: 'reference' },
              {
                title: t('vin'),
                render: (_, row: ImportedReference) => {
                  const end = inferEndVin(row.startVin, row.vehicleCount);
                  if (!row.startVin) return '—';
                  if (!end || end === row.startVin) return row.startVin;
                  return `${row.startVin} – ${end}`;
                },
              },
              { title: t('quantity'), dataIndex: 'vehicleCount', width: 100 },
              { title: t('startVin'), dataIndex: 'startVin' },
              {
                title: t('endVin'),
                render: (_, row: ImportedReference) =>
                  inferEndVin(row.startVin, row.vehicleCount) || '—',
              },
              {
                title: t('referenceType'),
                width: 180,
                render: (_, row: ImportedReference) => (
                  <Form.Item
                    name={['types', row.reference.toUpperCase()]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      allowClear
                      style={{ width: '100%' }}
                      placeholder={t('referenceType')}
                      options={REFERENCE_TYPES}
                    />
                  </Form.Item>
                ),
              },
            ]}
          />
        ) : null}
      </Form>
    </Modal>
  );
};
